import type { AxiosInstance } from 'axios';

// How many requests are on their way to the server right now.
//
// One number for the whole app, kept outside React, so the line under the top bar can
// tell when a page's data has arrived without any page having to say so. It is read with
// useSyncExternalStore(subscribe, getSnapshot).
//
// It only ever watches. Every config, response and error goes on exactly as it came,
// and the same error is thrown again, so nothing a page does with a request changes.

let inFlight = 0;
const listeners = new Set<() => void>();

const set = (next: number): void => {
  // Never below zero, whatever happens to an answer on its way back.
  const value = Math.max(0, next);
  if (value === inFlight) return;
  inFlight = value;
  for (const listener of [...listeners]) {
    // A listener that fails must never fail the request that was being counted.
    try {
      listener();
    } catch {
      /* the count stands; the request goes on */
    }
  }
};

/** Called whenever the number changes. Returns the way to stop listening. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The number of requests in flight. */
export function getSnapshot(): number {
  return inFlight;
}

// The requests that have been counted and not yet counted back. The config is marked
// here, not written on, so it goes on untouched; and an answer is counted back only
// while its config is still marked, so nothing is counted down twice.
// Each is marked with the round it was counted in (see forgetRequestsInFlight).
const counted = new WeakMap<object, number>();
let round = 0;
// One pair of interceptors per instance, however often it is asked for.
const tracked = new WeakSet<object>();

const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

const settle = (config: unknown): void => {
  if (isObject(config)) {
    const countedIn = counted.get(config);
    if (countedIn === undefined) return;
    counted.delete(config);
    // Counted before the count was last forgotten: it is not in the number any more.
    if (countedIn === round) set(inFlight - 1);
    return;
  }
  // No config at all: the request failed before it was sent (its body could not be
  // written out, say), so the error is not one of axios's and names no request. It was
  // counted when it was handed on, and without this it would stay counted for good.
  set(inFlight - 1);
};

/**
 * Starts the count again from zero. The line under the top bar calls this when it gives
 * up waiting (its longest run). If a request was ever counted and never counted back (a
 * later interceptor that retries with a copy of the config could do it), the number
 * would otherwise stay above zero and the line would run its full length on every page
 * until a reload. Requests still on their way are simply no longer waited for: when they
 * settle they are not counted back, so the number cannot go wrong the other way.
 */
export function forgetRequestsInFlight(): void {
  round += 1;
  set(0);
}

/**
 * Counts the requests of one axios instance: up when a request is handed on, down when
 * it settles, answered or refused. Adds one request interceptor and one response
 * interceptor to the instance, once.
 */
export function trackRequests(instance: AxiosInstance): void {
  if (tracked.has(instance)) return;
  tracked.add(instance);

  instance.interceptors.request.use((config) => {
    if (isObject(config) && !counted.has(config)) {
      counted.set(config, round);
      set(inFlight + 1);
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      settle((response as { config?: unknown } | null | undefined)?.config);
      return response;
    },
    (error) => {
      settle((error as { config?: unknown } | null | undefined)?.config);
      // The same error, thrown again: what the caller catches is what was thrown.
      return Promise.reject(error);
    },
  );
}
